import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { SelectOptions, SmallSelect } from '@/components/small-select';
import {
  OrButton,
  QBCaption,
  QBRemove,
  QBColon,
  QBTextInput,
  QBVSpace,
  shouldShowOrCondition,
  UrlParamsCheckbox,
} from '@/components/query-builder/shared';
import { QBContext } from '@/components/query-builder/context';
import { MultiValueOps } from '@/components/query-builder/multi-value';
import { Flexbox } from '@/components/flex';
import { QBTimeframeSuffix } from '@/components/query-builder/timeframe-suffix';
import { QBItemSelection } from '@/components/query-builder/models';
import { hash } from '@/utils/cache';

export interface UrlSuggestion {
  label: string;
  url: string;
}

export interface UrlSuggestionsCtx {
  getSuggestions?: (value: string, row?: QBItemSelection) => UrlSuggestion[];
}

export const UrlSuggestionsContext = React.createContext<UrlSuggestionsCtx>({});

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

const getDropdownPosition = (input: Element): DropdownPosition => {
  const { bottom, left, width } = input.getBoundingClientRect();
  return { top: bottom + 4, left, width };
};

export enum TextValueOps {
  IS = `is`,
  ISNOT = `isnot`,
  INCLUDES = `includes`,
  NOT_INCLUDES = `notincludes`,
  REGEX = `regex`,
}

export const textValueOptions: SelectOptions[] = [
  {
    key: TextValueOps.IS,
    value: `Is`,
  },
  {
    key: TextValueOps.ISNOT,
    value: `Is not`,
  },
  {
    key: TextValueOps.INCLUDES,
    value: `Includes`,
  },
  {
    key: TextValueOps.NOT_INCLUDES,
    value: `Not includes`,
  },
  {
    key: TextValueOps.REGEX,
    value: `Matches`,
  },
];

export function stripMetafieldPrefix(
  value: string,
  metafieldKey?: string,
): string {
  if (!metafieldKey || !value) return value;
  const prefix = `${metafieldKey}.`;
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export function withMetafieldPrefix(
  value: string,
  metafieldKey?: string,
): string {
  if (!metafieldKey || !value) return value;
  return `${metafieldKey}.${value}`;
}

export function QBTextInputWrapper({
  childIdx,
  current,
  onChange,
  onMouseEnter,
  onSelectValue,
  idx,
  largeText,
}: {
  current: QBItemSelection;
  childIdx: number;
  onChange: (ev) => void;
  onMouseEnter: () => void;
  onSelectValue?: (value: string) => void;
  idx: number;
  largeText?: boolean;
}) {
  const { values, qbProps } = current;
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>();
  const { getSuggestions } = useContext(UrlSuggestionsContext);
  const [suggestions, setSuggestions] = useState<UrlSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>();

  useEffect(() => {
    setTimeout(() => {
      const end = ref.current?.value?.length;
      if (typeof end === `undefined`) return;
      ref.current.setSelectionRange(end, end);
      ref.current.focus();
    }, 1000);
  }, []);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const updatePosition = () =>
      setDropdownPosition(getDropdownPosition(ref.current));
    window.addEventListener(`scroll`, updatePosition, true);
    window.addEventListener(`resize`, updatePosition);
    return () => {
      window.removeEventListener(`scroll`, updatePosition, true);
      window.removeEventListener(`resize`, updatePosition);
    };
  }, [showSuggestions]);

  const openSuggestions = (value: string) => {
    if (!getSuggestions) return;
    setSuggestions(getSuggestions(value, current));
    setDropdownPosition(getDropdownPosition(ref.current));
    setShowSuggestions(true);
  };

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    onChange(ev);
    openSuggestions(ev.target.value);
  };

  const handleSuggestionSelect = (suggestion: UrlSuggestion) => {
    onSelectValue?.(suggestion.url);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const displayValue = stripMetafieldPrefix(
    values?.[childIdx]?.value,
    qbProps?.metafieldKey,
  );

  return (
    <InputWrapper>
      <QBTextInput
        as={largeText ? `textarea` : `input`}
        $largeText={largeText}
        onMouseEnter={onMouseEnter}
        placeholder={qbProps?.placeholder}
        data-tip={displayValue || ``}
        data-for={`text-${hash(displayValue || ``)}-${childIdx}-${idx}`}
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onClick={() => openSuggestions(displayValue || ``)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
      />
      {showSuggestions &&
        suggestions.length > 0 &&
        ReactDOM.createPortal(
          <SuggestionDropdown style={dropdownPosition}>
            {suggestions.map((s) => {
              const isSelected = s.url === displayValue;
              return (
                <SuggestionItem
                  key={s.url}
                  onMouseDown={() => handleSuggestionSelect(s)}
                >
                  <SuggestionLabel $selected={isSelected}>
                    {s.label}
                  </SuggestionLabel>
                </SuggestionItem>
              );
            })}
          </SuggestionDropdown>,
          document.body,
        )}
    </InputWrapper>
  );
}

export function QBTextValue({
  idx,
  textCondNoMarginTop,
}: {
  idx: number;
  textCondNoMarginTop?: boolean;
}) {
  const {
    appendValue,
    updateValue,
    queryState,
    setIncludeQueryParams,
    removeValue,
    removeKey,
    updateTimeframe,
  } = useContext(QBContext);
  const current = queryState[idx];

  const onOpChanged = (childIdx, selected) => {
    updateValue(idx, childIdx, selected, current?.values?.[childIdx]?.value);
  };

  const onValueChanged = (childIdx, ev) => {
    updateValue(
      idx,
      childIdx,
      current?.values?.[childIdx]?.op,
      withMetafieldPrefix(ev.target.value, current?.qbProps?.metafieldKey),
    );
  };

  const onAddMore = () => {
    appendValue(
      idx,
      current?.qbProps?.defaultTextValueOp || MultiValueOps.IS,
      ``,
    );
  };

  function isOpDisabled(queryState: QBItemSelection[], idx: number) {
    return !!queryState?.[idx]?.qbProps?.disableOp;
  }
  const onRemove = (childIdx: number) => {
    if (current.values.length === 1) {
      removeKey(idx);
    } else {
      removeValue(idx, childIdx);
    }
  };
  const [isShowTooltip, setIsShowTooltip] = useState(false);
  return (
    <Wrapper>
      {current?.values?.map((_, childIdx) => {
        const displayValue = stripMetafieldPrefix(
          current?.values[childIdx].value,
          current?.qbProps?.metafieldKey,
        );
        return (
          <Row
            key={`text-value-${childIdx}`}
            textCondNoMarginTop={textCondNoMarginTop || childIdx === 0}
            $largeText={current?.qbProps?.largeText}
          >
            {childIdx > 0 && (
              <LogicalOr $largeText={current?.qbProps?.largeText}>Or</LogicalOr>
            )}
            {!current?.qbProps?.hideOp && (
              <>
                <SmallSelect
                  minWidth={70}
                  isDisabled={isOpDisabled(queryState, idx)}
                  options={
                    current?.qbProps?.restrictedTextOps
                      ? textValueOptions.filter((o) =>
                          current.qbProps.restrictedTextOps.includes(
                            o.key as TextValueOps,
                          ),
                        )
                      : textValueOptions
                  }
                  defaultValue={
                    current?.values?.[childIdx]?.op ||
                    current?.qbProps?.defaultTextValueOp
                  }
                  onSelection={(selected) => onOpChanged(childIdx, selected)}
                />
                <QBColon />
              </>
            )}
            <QBTextInputWrapper
              largeText={current?.qbProps?.largeText}
              idx={idx}
              current={current}
              childIdx={childIdx}
              onMouseEnter={() => setIsShowTooltip(true)}
              onChange={(ev) => onValueChanged(childIdx, ev)}
              onSelectValue={(value) =>
                updateValue(
                  idx,
                  childIdx,
                  current?.values?.[childIdx]?.op,
                  withMetafieldPrefix(value, current?.qbProps?.metafieldKey),
                )
              }
            />
            {!current?.qbProps?.largeText && (
              <div>
                {displayValue && displayValue.length > 23 && (
                  <ReactTooltip
                    id={`text-${hash(displayValue || ``)}-${childIdx}-${idx}`}
                    effect="float"
                    place="top"
                    disable={!isShowTooltip}
                    backgroundColor="#fff"
                    textColor="#000"
                    className="text-tooltip"
                  >
                    <span>{displayValue || ``}</span>
                  </ReactTooltip>
                )}
              </div>
            )}
            <QBRemove
              shouldHide={
                queryState?.length <= 1 && current?.values?.length === 1
              }
              onClick={() => onRemove(childIdx)}
            />
          </Row>
        );
      })}
      <UrlParamsCheckbox
        mt="1rem"
        current={current}
        onSelection={(bool) => setIncludeQueryParams(idx, bool)}
      />
      <QBVSpace />
      {queryState[idx].qbProps.hasTimeframe && (
        <QBTimeframeSuffix
          onChange={(v) => updateTimeframe(idx, v)}
          initialValue={queryState[idx].timeframe}
        />
      )}
      {shouldShowOrCondition(current) && (
        <OrButton onClick={onAddMore}>|| Or</OrButton>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Flexbox)`
  && {
    flex-direction: column;
  }
`;

const Row = styled.div<{ textCondNoMarginTop?: boolean; $largeText?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  ${(p) => p.$largeText && `align-items: flex-start;`}

  margin-top: ${(p) => (p.textCondNoMarginTop ? 0 : `1.5rem`)};
`;

const LogicalOr = styled(QBCaption)<{ $largeText?: boolean }>`
  && {
    position: absolute;
    top: ${(p) => (p.$largeText ? `1rem` : `calc(50% - 1rem)`)};
    left: -3rem;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SuggestionDropdown = styled.ul`
  position: fixed;
  z-index: 9999999999999999;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.8rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 0.4rem 0;
  max-height: 28rem;
  overflow-x: hidden;
  overflow-y: auto;
`;

const SuggestionItem = styled.li`
  display: flex;
  flex-direction: column;
  padding: 1.1rem 1.6rem;
  cursor: pointer;
  transition: background 0.1s ease;

  &&:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  &&:hover {
    background: #f5f6f7;
  }
`;

const SuggestionLabel = styled.span<{ $selected?: boolean }>`
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${(p) => (p.$selected ? `#3451fa` : `#2d3748`)};
  word-break: break-all;
`;
